import React, { useEffect, useState } from "react";
import {
  FaPlus, FaLayerGroup, FaUserFriends, FaUserShield,
  FaCheck, FaTrashAlt, FaChevronRight, FaTimes, FaUserPlus, FaSearch, FaFilter
} from "react-icons/fa";

// Icons
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const COLORS = {
  DIVIDER: "#E6EEF2",
  BORDER: "rgba(226,232,240,1)",
  BABY1: "#AAD4F1",
};

const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Configuration for Filters based on Logic
// MRN -> Warehouse & Branch
// Others -> Project & Feeder
const FILTER_CONFIG_MAP = {
  'MRN_MASTER': [
    { key: 'WAREHOUSE', label: 'Location', endpoint: '/locations', idField: 'LocationCode', labelField: 'LocationName' },
    { key: 'Branch', label: 'Branch', endpoint: '/branches', idField: 'BranchCode', labelField: 'BranchName' },
    { key: 'ProjectList', label: 'Project', endpoint: '/project', idField: 'No', labelField: 'Description' } 
  ],
  'DEFAULT': [
    { key: 'ProjectList', label: 'Project', endpoint: '/project', idField: 'No', labelField: 'Description' },
    { key: 'Feeder', label: 'Feeder', endpoint: '/feeder', idField: 'Code', labelField: 'Description' }
  ]
};

const ApprovalFormModal = ({ open, onClose, apiBaseUrl, fetchWorkflows, editingFlowId }) => {
  const [formData, setFormData] = useState({ FlowName: '', DocumentType: 'MRN_MASTER',IsActive: true, levels: [] });
  const [loading, setLoading] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [levelSearchTerms, setLevelSearchTerms] = useState({});
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);

  // --- Filter States ---
  const [flowFilters, setFlowFilters] = useState([]); // [{ FilterMaster: 'Project', Values: ['P01'] }]
  const [filterOptions, setFilterOptions] = useState({}); // Cache for dropdown data
  const [activeFilterDropdown, setActiveFilterDropdown] = useState(null); // 'type_index' or 'value_index'
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
   const [loadingFilters, setLoadingFilters] = useState(false);
  const [highlightedFilterIndex, setHighlightedFilterIndex] = useState(-1);

  // Get available filter types based on DocumentType
  const availableFilterTypes = FILTER_CONFIG_MAP[formData.DocumentType] || FILTER_CONFIG_MAP['DEFAULT'];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        setActiveFilterDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
    }
  }, [open]);

  const fetchDropdownData = async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        fetch(`${apiBaseUrl}/user`, { headers: getAuthHeader() }),
        fetch(`${apiBaseUrl}/profiles-name`, { headers: getAuthHeader() })
      ]);
      const uData = await uRes.json();
      const pData = await pRes.json();
      setUsers(Array.isArray(uData) ? uData : []);
      setProfiles(Array.isArray(pData) ? pData : []);
    } catch (err) {
      console.error("Error fetching dropdowns", err);
    }
  };

  // Reset filters if DocumentType changes (only for new flows)
  useEffect(() => {
    if (!editingFlowId) {
      setFlowFilters([]);
      setFilterOptions({});
    }
  }, [formData.DocumentType, editingFlowId]);

  useEffect(() => {
    const resetForm = () => {
      setFormData({ FlowName: '', DocumentType: 'MRN_MASTER',IsActive: true, levels: [] });
      setIsMaximized(false);
      setLevelSearchTerms({});
      setFlowFilters([]);
      setFilterOptions({});
    };

    if (open) {
      if (editingFlowId) {
        loadFlowData(editingFlowId);
      } else {
        resetForm();
      }
    }
  }, [open, editingFlowId]);

  // --- FILTER LOGIC ---

  // Fetch available values for a specific filter type
  // This API returns values NOT used in ACTIVE flows of the same screen
  const fetchFilterValues = async (filterKey) => {
    if (filterOptions[filterKey]) return; // Cache check

    setLoadingFilters(true);
    try {
      // We pass DocumentType and FilterMaster so backend can exclude active flow values
      const response = await fetch(
        `${apiBaseUrl}/approval-filters/available-values?documentType=${formData.DocumentType}&filterType=${filterKey}${editingFlowId ? `&excludeFlowId=${editingFlowId}` : ''}`, 
        { headers: getAuthHeader() }
      );
      const data = await response.json();
      setFilterOptions(prev => ({ ...prev, [filterKey]: data }));
    } catch (error) {
      console.error('Error fetching filter values:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const handleAddFilter = () => {
    // Limit filters to available types count
    if (flowFilters.length >= availableFilterTypes.length) return;
    setFlowFilters(prev => [...prev, { FilterMaster: '', Values: [] }]);
  };

  const handleRemoveFilter = (index) => {
    setFlowFilters(prev => prev.filter((_, i) => i !== index));
  };

  const handleFilterTypeSelect = (index, typeKey) => {
    const newFilters = [...flowFilters];
    newFilters[index] = { FilterMaster: typeKey, Values: [] };
    setFlowFilters(newFilters);
    setActiveFilterDropdown(null);
    fetchFilterValues(typeKey);
  };

  const handleFilterValueToggle = (index, valueId) => {
    const newFilters = [...flowFilters];
    const filter = newFilters[index];
    const exists = filter.Values.includes(valueId);
    
    if (exists) {
      filter.Values = filter.Values.filter(v => v !== valueId);
    } else {
      filter.Values.push(valueId);
    }
    setFlowFilters([...newFilters]);
  };

  // --- LOAD DATA FOR EDIT ---
 const loadFlowData = async (id) => {
    setIsLoadingEdit(true);
    try {
      const res = await fetch(`${apiBaseUrl}/approval-flows/${id}`, { headers: getAuthHeader() });
      const data = await res.json();
      
      // 1. Set Header and Levels
      setFormData({
        FlowName: data.FlowName,
        DocumentType: data.DocumentType,
        IsActive: data.IsActive === true || data.IsActive === 1,
        levels: data.levels.map(l => ({
          ApprovalLevelID: l.ApprovalLevelID, 
          LevelNumber: l.LevelNumber,
          ApprovalType: l.ApprovalType,
          Approvers: l.Approvers || [],
          expanded: true
        }))
      });

      // 2. Set Filters (NEW LOGIC)
      if (data.filters && Array.isArray(data.filters)) {
        setFlowFilters(data.filters);
        
        // IMPORTANT: Pre-fetch options for loaded filters so the Tags show "Code - Name"
        // instead of just the ID.
        data.filters.forEach(f => {
            if(f.FilterMaster) fetchFilterValues(f.FilterMaster);
        });
      } else {
        setFlowFilters([]);
      }

    } catch (err) {
      console.error("Failed to load flow", err);
      alert("Could not load flow details");
    } finally {
      setIsLoadingEdit(false);
    }
  };

  // --- STANDARD LOGIC ---

  const addLevel = () => {
    setFormData({
      ...formData,
      levels: [
        ...formData.levels,
        {
          LevelNumber: formData.levels.length + 1,
          ApprovalType: 'USER_WISE',
          Approvers: [],
          expanded: true
        }
      ]
    });
  };

  const removeLevel = (index) => {
    const newLevels = formData.levels.filter((_, i) => i !== index).map((l, i) => ({ ...l, LevelNumber: i + 1 }));
    setFormData({ ...formData, levels: newLevels });
  };

  const updateLevelType = (index, type) => {
    const newLevels = [...formData.levels];
    newLevels[index].ApprovalType = type;
    newLevels[index].Approvers = []; 
    newLevels[index].expanded = true;
    setFormData({ ...formData, levels: newLevels });
  };

  const toggleApprover = (levelIndex, id) => {
    const newLevels = [...formData.levels];
    const currentApprovers = newLevels[levelIndex].Approvers;
    
    if (!currentApprovers.includes(id)) {
        if (currentApprovers.length >= 3) {
            alert("You can select a maximum of 3 approvers per level.");
            return;
        }
        newLevels[levelIndex].Approvers = [...currentApprovers, id];
    } else {
      newLevels[levelIndex].Approvers = currentApprovers.filter(i => i !== id);
    }
    setFormData({ ...formData, levels: newLevels });
  };

  const toggleExpandLevel = (index) => {
      const newLevels = [...formData.levels];
      newLevels[index].expanded = !newLevels[index].expanded;
      setFormData({...formData, levels: newLevels});
  }

     const handleSubmit = async () => {
    // 1. Validate Workflow Name
    if (!formData.FlowName) {
      alert("Please enter a Workflow Name.");
      return;
    }

    // 2. Validate Filters (Mandatory)
    if (flowFilters.length === 0) {
      alert("Please add at least one Data Scope Filter.");
      return;
    }

    // 3. Validate Filter Values
    for (const filter of flowFilters) {
      if (!filter.FilterMaster) {
        alert("Please select a Filter Type for all added filters.");
        return;
      }
      if (filter.Values.length === 0) {
        const config = availableFilterTypes.find(t => t.key === filter.FilterMaster);
        alert(`Please select at least one value for the "${config?.label}" filter.`);
        return;
      }
    }

    // 4. Validate Levels Exist (At least Level 1)
    if (formData.levels.length === 0) {
      alert("Please add at least one approval level.");
      return;
    }

    // 5. Validate Approvers (At least 1 person in every level)
    const emptyLevel = formData.levels.find(level => level.Approvers.length === 0);
    if (emptyLevel) {
      alert(`Please select at least one approver for Level ${emptyLevel.LevelNumber}.`);
      return;
    }

    setLoading(true);
    try {
      // --- CONSTRUCT SINGLE PAYLOAD ---
      
      // Map levels to include approvers details directly
      const levelsPayload = formData.levels.map(level => ({
        LevelNumber: level.LevelNumber,
        ApprovalType: level.ApprovalType,
        Approvers: level.Approvers.map(appId => ({
          ApproverType: level.ApprovalType === 'USER_WISE' ? 'USER' : 'ROLE',
          UserID: level.ApprovalType === 'USER_WISE' ? appId : null,
          RoleID: level.ApprovalType === 'ROLE_WISE' ? appId : null
        }))
      }));

      const payload = {
        FlowName: formData.FlowName,
        DocumentType: formData.DocumentType,
         IsActive: formData.IsActive,
        CreatedBy: 1, // Replace with actual user ID context
        HasFilters: flowFilters.length > 0,
        filters: flowFilters, // Format: [{ FilterMaster: 'Project', Values: ['P01'] }]
        levels: levelsPayload  // Format: [{ LevelNumber: 1, Approvers: [...] }]
      };

      // --- CALL SINGLE API ---
      if (editingFlowId) {
        // UPDATE Logic (Assuming you update this endpoint later to handle full update)
        // For now, keeping your existing check logic separate for updates
        const checkRes = await fetch(`${apiBaseUrl}/approval-flows/${editingFlowId}/check-status`, {
            headers: getAuthHeader()
        });
        const checkData = await checkRes.json();

        if (!checkData.canEdit) {
            alert("Action Denied: You first need to complete the approval of the pending document(s) and then you can update the approval flow.");
            setLoading(false);
            return;
        }

        // Note: You should eventually merge the Update logic into one API as well
        const updateRes = await fetch(`${apiBaseUrl}/approval-flows/${editingFlowId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload) 
        });
        
        if (!updateRes.ok) {
            const errData = await updateRes.json();
            throw new Error(errData.message || 'Failed to update');
        }

      } else {
        // CREATE - Single Call
        const flowRes = await fetch(`${apiBaseUrl}/approval-flow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload)
        });
        
        if (!flowRes.ok) {
          const errorData = await flowRes.json();
          throw new Error(errorData.message || 'Error creating workflow');
        }
      }

      fetchWorkflows();
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to save workflow.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black bg-opacity-50 transition-opacity duration-300">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {isLoadingEdit && (
        <div className="absolute inset-0 z-50 bg-white bg-opacity-80 flex flex-col items-center justify-center">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
             <p className="text-blue-900 font-bold">Loading Flow Configuration...</p>
        </div>
      )}

      <div className={`relative z-10 bg-white shadow-2xl w-full flex flex-col transition-all duration-300 ease-in-out m-auto
        ${isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'}
      `}>
        {/* Header */}
        <div className="flex-shrink-0 p-5 border-b flex justify-between items-center" style={{ borderColor: COLORS.BORDER, backgroundColor: "#F8FAFC" }}>
          <div>
            <h3 className="text-xl font-bold text-slate-900">
                {editingFlowId ? 'Edit Approval Workflow' : 'Create Approval Workflow'}
            </h3>
            <p className="text-sm text-gray-500">Define approval steps and data scope.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setIsMaximized(!isMaximized)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors">
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors">
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">Workflow Name</label>
              <input 
                type="text" 
                className="w-full p-3 rounded border focus:ring-2 focus:ring-blue-200 outline-none transition-all" 
                style={{ borderColor: COLORS.BORDER }} 
                value={formData.FlowName} 
                onChange={e => setFormData({ ...formData, FlowName: e.target.value })} 
                placeholder="e.g., MRN Order Approval" 
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Document Type</label>
              <select 
                className="w-full p-3 rounded border focus:ring-2 focus:ring-blue-200 outline-none transition-all" 
                style={{ borderColor: COLORS.BORDER }} 
                value={formData.DocumentType} 
                onChange={e => setFormData({ ...formData, DocumentType: e.target.value })}
              >
                <option value="MRN_MASTER">Material Receipt Note (MRN)</option>
                <option value="SURVEY_BOQ">Survey BOQ</option>
                <option value="SITE_INDENT">Site Indent</option>
                <option value="MATERIAL_ISSUE_INDENT">Material Issue Indent</option>
                <option value="MATERIAL_RECEIVE_INDENT">Material Receive Indent</option>
                <option value="DPR">Daily Progress Report (DPR)</option>
                <option value="MATERIAL_RETURN">Material Return</option>
              </select>
            </div>
            {/* Inside the grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
              <select 
                className="w-full p-3 rounded border focus:ring-2 focus:ring-blue-200 outline-none transition-all" 
                style={{ borderColor: COLORS.BORDER }} 
                value={formData.IsActive ? 'Active' : 'Inactive'} 
                onChange={e => setFormData({ ...formData, IsActive: e.target.value === 'Active' })}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* --- NEW FILTER SECTION --- */}
          <div className="bg-white border rounded-lg shadow-sm mb-6 p-6" style={{ borderColor: COLORS.BORDER }}>
             <div className="flex justify-between items-center mb-4">
                <div>
                    <h4 className="text-md font-bold text-slate-800 flex items-center gap-2">
                        <FaFilter /> Data Scope Filters
                    </h4>
                    <p className="text-xs text-gray-500">Limit this workflow to specific data. If not set, applies to all data.</p>
                </div>
                 <button
                    type="button"
                    onClick={handleAddFilter}
                    disabled={flowFilters.length >= availableFilterTypes.length}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-sm font-medium ${flowFilters.length >= availableFilterTypes.length ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                    <FaPlus /> Add Filter
                </button>
             </div>

             {flowFilters.length === 0 && (
                 <div className="text-center py-4 text-gray-400 border-dashed border rounded bg-gray-50">
                     No filters applied. 
                 </div>
             )}

                         <div className="space-y-4">
                {flowFilters.map((filter, index) => {
                    const config = availableFilterTypes.find(t => t.key === filter.FilterMaster);
                    const options = filterOptions[filter.FilterMaster] || [];
                    
                    // Filter options early for keyboard navigation
                    const filteredOptions = options.filter(opt => 
                        String(opt[config?.idField] || '').toLowerCase().includes(filterSearchTerm.toLowerCase()) ||
                        String(opt[config?.labelField] || '').toLowerCase().includes(filterSearchTerm.toLowerCase())
                    );

                    return (
                        <div key={index} className="flex gap-4 items-start bg-gray-50 p-4 border rounded-lg">
                            {/* Filter Type Dropdown */}
                            <div className="w-1/3 relative filter-dropdown-container">
                                <label className="block text-xs font-bold text-gray-500 mb-1">Filter Type</label>
                                <div 
                                    className="w-full border rounded p-2 cursor-pointer flex justify-between items-center bg-white"
                                    tabIndex={0}
                                    onClick={() => {
                                        setActiveFilterDropdown(activeFilterDropdown === `type_${index}` ? null : `type_${index}`);
                                        setHighlightedFilterIndex(-1);
                                    }}
                                    onKeyDown={(e) => {
                                        // Keyboard nav for Type Dropdown
                                        const availableTypes = availableFilterTypes.filter(t => !flowFilters.some(f => f.FilterMaster === t.key && f !== filter));
                                        
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setHighlightedFilterIndex(prev => (prev < availableTypes.length - 1 ? prev + 1 : prev));
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setHighlightedFilterIndex(prev => (prev > 0 ? prev - 1 : 0));
                                        } else if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (highlightedFilterIndex >= 0 && availableTypes[highlightedFilterIndex]) {
                                                handleFilterTypeSelect(index, availableTypes[highlightedFilterIndex].key);
                                                setHighlightedFilterIndex(-1);
                                            }
                                        }
                                    }}
                                >
                                    <span className={filter.FilterMaster ? 'text-gray-800' : 'text-gray-400'}>
                                        {config?.label || 'Select Type'}
                                    </span>
                                    <FaChevronRight className={`transition-transform ${activeFilterDropdown === `type_${index}` ? 'rotate-90' : ''}`} />
                                </div>
                                {activeFilterDropdown === `type_${index}` && (
                                    <div className="absolute z-20 mt-1 w-full bg-white border shadow-lg rounded-md">
                                        {availableFilterTypes
                                            .filter(t => !flowFilters.some(f => f.FilterMaster === t.key && f !== filter))
                                            .map((t, i) => (
                                                <div 
                                                    key={t.key} 
                                                    className={`p-2 cursor-pointer text-sm ${highlightedFilterIndex === i ? 'bg-blue-100' : 'hover:bg-blue-50'}`} 
                                                    onClick={() => handleFilterTypeSelect(index, t.key)}
                                                >
                                                    {t.label}
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>

                            {/* Values Dropdown */}
                            <div className="flex-1 relative filter-dropdown-container">
                                <label className="block text-xs font-bold text-gray-500 mb-1">Values</label>
                                
                                {/* Tags */}
                                <div className="flex flex-wrap gap-1 mb-1 min-h-[24px]">
                                    {filter.Values.map(val => {
                                        const item = options.find(o => o[config?.idField] === val);
                                        const displayText = item 
                                        ? `${item[config?.idField]} - ${item[config?.labelField]}` 
                                        : val;
                                        return (
                                            <span key={val} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                                                 {displayText}
                                                <button onClick={() => handleFilterValueToggle(index, val)} className="font-bold hover:text-red-600">×</button>
                                            </span>
                                        )
                                    })}
                                </div>

                                <input
                                    type="text"
                                    placeholder="Search to select..."
                                    className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={activeFilterDropdown === `value_${index}` ? filterSearchTerm : ''}
                                    onChange={(e) => { 
                                        setActiveFilterDropdown(`value_${index}`); 
                                        setFilterSearchTerm(e.target.value);
                                        setHighlightedFilterIndex(-1);
                                    }}
                                    onFocus={() => { 
                                        setActiveFilterDropdown(`value_${index}`); 
                                        setFilterSearchTerm('');
                                        setHighlightedFilterIndex(-1);
                                    }}
                                    onKeyDown={(e) => {
                                        // Keyboard nav for Value Dropdown
                                        if (activeFilterDropdown !== `value_${index}`) return;
                                        
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setHighlightedFilterIndex(prev => 
                                                prev < filteredOptions.length - 1 ? prev + 1 : prev
                                            );
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setHighlightedFilterIndex(prev => prev > 0 ? prev - 1 : 0);
                                        } else if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (highlightedFilterIndex >= 0 && filteredOptions[highlightedFilterIndex]) {
                                                const selectedItem = filteredOptions[highlightedFilterIndex];
                                                handleFilterValueToggle(index, selectedItem[config.idField]);
                                                setHighlightedFilterIndex(-1);
                                            }
                                        }
                                    }}
                                />

                                {activeFilterDropdown === `value_${index}` && (
                                    <div className="absolute z-20 mt-1 w-full bg-white border shadow-lg rounded-md max-h-48 overflow-y-auto">
                                        {loadingFilters ? (
                                            <div className="p-3 text-gray-400 text-sm">Loading...</div>
                                        ) : (
                                            filteredOptions.map((opt, optIdx) => {
                                                const key = opt[config.idField];
                                                const isChecked = filter.Values.includes(key);
                                                return (
                                                    <div
                                                        key={key}
                                                        className={`p-2 cursor-pointer text-sm flex items-center gap-2 
                                                            ${isChecked ? 'bg-blue-50' : ''} 
                                                            ${optIdx === highlightedFilterIndex ? 'bg-blue-100 ring-1 ring-blue-400' : 'hover:bg-gray-50'}`}
                                                        onClick={() => handleFilterValueToggle(index, key)}
                                                    >
                                                        <input type="checkbox" checked={isChecked} onChange={() => {}} className="pointer-events-none" />
                                                        <span className="font-medium">{key}</span>
                                                        <span className="text-gray-500 truncate">- {opt[config.labelField]}</span>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Remove Button */}
                            <div className="pt-5">
                                <button type="button" onClick={() => handleRemoveFilter(index)} className="text-red-400 hover:text-red-600 p-1">
                                    <FaTrashAlt />
                                </button>
                            </div>
                        </div>
                    )
                })}
             </div>
          </div>

          {/* Approval Levels Section */}
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-md font-bold text-slate-800 flex items-center gap-2">
                <FaLayerGroup /> Approval Sequence
              </h4>
              <button onClick={addLevel} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded shadow-md transition-all">
                <FaPlus /> Add Level
              </button>
            </div>

            {formData.levels.length === 0 && (
              <div className="text-center py-12 bg-gray-50 border-2 border-dashed rounded-lg border-gray-300">
                <p className="text-gray-500 font-medium">No levels configured. Add a level to start.</p>
              </div>
            )}

            {formData.levels.map((level, idx) => (
              <div key={idx} className="bg-white border rounded-lg shadow-sm overflow-hidden" style={{ borderColor: COLORS.BORDER }}>
                {/* Level Header */}
                <div className="flex justify-between items-center p-4 bg-gray-50 border-b" style={{ borderColor: COLORS.DIVIDER }}>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                      {level.LevelNumber}
                    </span>
                    <span className="font-bold text-gray-700">Level {level.LevelNumber}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleExpandLevel(idx)} className="text-xs font-bold flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded hover:bg-gray-100 transition-colors text-gray-700">
                        {level.expanded ? 'Hide Selection' : 'Edit Selection'}
                        <FaChevronRight className={`w-3 h-3 transition-transform ${level.expanded ? 'rotate-90' : ''}`} />
                    </button>
                    <button onClick={() => removeLevel(idx)} className="text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded hover:bg-red-50 transition-colors">
                        <FaTrashAlt /> Remove
                    </button>
                  </div>
                </div>

                {/* Level Body */}
                {level.expanded ? (
                <div className="border-t" style={{ borderColor: COLORS.DIVIDER }}>
                  <div className="mb-4 p-6 pb-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Approval Based On</label>
                    <div className="flex bg-gray-100 p-1 rounded-lg w-full md:w-1/2">
                      <button onClick={() => updateLevelType(idx, 'USER_WISE')} className={`flex-1 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition-all ${level.ApprovalType === 'USER_WISE' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <FaUserFriends /> Specific Users
                      </button>
                      <button onClick={() => updateLevelType(idx, 'ROLE_WISE')} className={`flex-1 py-2 rounded-md text-sm font-bold flex items-center justify-center gap-2 transition-all ${level.ApprovalType === 'ROLE_WISE' ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        <FaUserShield /> Roles
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 pt-2">
                    <div className="lg:col-span-2 space-y-3">
                       <div className="flex justify-between items-center mb-2">
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Select Approvers</label>
                          <div className="relative w-64">
                             <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                             <input type="text" placeholder="Search..." className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" value={levelSearchTerms[idx] || ''} onChange={(e) => setLevelSearchTerms(prev => ({ ...prev, [idx]: e.target.value }))} />
                          </div>
                       </div>
                       <div className="h-64 overflow-y-auto border rounded-lg bg-white">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2">
                            {level.ApprovalType === 'USER_WISE' ? (
                              users.filter(u => (u.FullName || '').toLowerCase().includes((levelSearchTerms[idx] || '').toLowerCase())).map(user => {
                                const isSelected = level.Approvers.includes(user.UserID);
                                const isDisabled = !isSelected && level.Approvers.length >= 3;
                                return (
                                  <div key={user.UserID} onClick={() => !isDisabled && toggleApprover(idx, user.UserID)} className={`cursor-pointer border rounded-md p-3 flex items-center gap-3 transition-all group ${isDisabled ? 'opacity-40 cursor-not-allowed bg-gray-50' : ''} ${isSelected && !isDisabled ? 'bg-blue-50 border-blue-500' : !isDisabled && 'bg-white border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{user.FullName ? user.FullName.charAt(0) : 'U'}</div>
                                    <div className="min-w-0"><div className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>{user.FullName}</div><div className={`text-xs truncate ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}>{user.Username}-{user.Email}</div></div>
                                    {isSelected && !isDisabled && (<div className="ml-auto text-blue-600"><FaCheck className="w-4 h-4" /></div>)}
                                  </div>
                                );
                              })
                            ) : (
                              profiles.filter(p => (p.ProfileName || '').toLowerCase().includes((levelSearchTerms[idx] || '').toLowerCase())).map(profile => {
                                const isSelected = level.Approvers.includes(profile.ProfileID);
                                const isDisabled = !isSelected && level.Approvers.length >= 3;
                                return (
                                  <div key={profile.ProfileID} onClick={() => !isDisabled && toggleApprover(idx, profile.ProfileID)} className={`cursor-pointer border rounded-md p-3 flex items-center gap-3 transition-all group ${isDisabled ? 'opacity-40 cursor-not-allowed bg-gray-50' : ''} ${isSelected && !isDisabled ? 'bg-purple-50 border-purple-500' : !isDisabled && 'bg-white border-gray-200 hover:border-purple-300 hover:bg-gray-50'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}><FaUserShield /></div>
                                    <div className="min-w-0"><div className={`text-sm font-semibold truncate ${isSelected ? 'text-purple-900' : 'text-gray-700'}`}>{profile.ProfileName}</div></div>
                                    {isSelected && !isDisabled && (<div className="ml-auto text-purple-600"><FaCheck className="w-4 h-4" /></div>)}
                                  </div>
                                );
                              })
                            )}
                          </div>
                       </div>
                    </div>

                    <div className="lg:col-span-1">
                       <div className="bg-blue-50 rounded-lg border border-blue-100 p-4 h-full flex flex-col">
                          <div className="flex justify-between items-center mb-4">
                            <label className="block text-sm font-bold text-blue-900 uppercase tracking-wider">Selected</label>
                            <span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">{level.Approvers.length} / 3</span>
                          </div>
                          <div className="flex-1 space-y-3 overflow-y-auto custom-scrollbar pr-1">
                            {level.Approvers.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-blue-300 opacity-70"><FaUserPlus className="w-8 h-8 mb-2" /><span className="text-sm font-medium">No approvers selected</span></div>
                            ) : (
                                level.Approvers.map(approverId => {
                                    const user = level.ApprovalType === 'USER_WISE' ? users.find(u => u.UserID === approverId) : profiles.find(p => p.ProfileID === approverId);
                                    if (!user) return null;
                                    return (
                                        <div key={approverId} className="bg-white border border-blue-100 rounded-lg p-3 flex items-start gap-3 shadow-sm relative group">
                                            <button onClick={() => toggleApprover(idx, approverId)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500 transition-colors"><FaTimes className="w-3 h-3" /></button>
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${level.ApprovalType === 'USER_WISE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{user.FullName ? user.FullName.charAt(0) : <FaUserShield />}</div>
                                            <div className="overflow-hidden"><div className="text-sm font-bold text-gray-800 truncate">{user.FullName || user.ProfileName}</div><div className="text-xs text-gray-500 truncate">{user.Username || 'Role'}</div></div>
                                        </div>
                                    )
                                })
                            )}
                          </div>
                       </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4 px-6 pb-6"><button onClick={() => toggleExpandLevel(idx)} className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded shadow hover:bg-green-700 flex items-center gap-2"><FaCheck /> Done Selecting</button></div>
                </div>
                ) : (
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                    {level.Approvers.length > 0 ? (
                        <div className="flex flex-wrap gap-2 items-center">
                            <span className="text-xs font-bold text-gray-500 mr-2 uppercase">Selected Approvers:</span>
                            {level.Approvers.map(id => {
                                const user = level.ApprovalType === 'USER_WISE' ? users.find(u => u.UserID === id) : profiles.find(p => p.ProfileID === id);
                                if (!user) return null;
                                const isUser = level.ApprovalType === 'USER_WISE';
                                return (
                                    <div key={id} className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${isUser ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                                        <span className="w-4 h-4 flex items-center justify-center rounded-full bg-white/50 text-current">{isUser ? <FaUserFriends size={10} /> : <FaUserShield size={10} />}</span>
                                        <span className="truncate max-w-[150px]">{user.FullName || user.ProfileName}</span>
                                    </div>
                                )
                            })}
                        </div>
                    ) : <div className="text-sm text-gray-400 italic">No approvers selected for this level yet.</div>}
                </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-5 border-t bg-gray-50 flex justify-end gap-3" style={{ borderColor: COLORS.BORDER }}>
          <button onClick={onClose} className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded transition-all">Cancel</button>
          <button onClick={handleSubmit} disabled={loading || !formData.FlowName || formData.levels.length === 0} className="px-8 py-2.5 font-bold rounded shadow-lg transition-all flex items-center gap-2" style={{ backgroundColor: COLORS.BABY1, color: '#0F172A', opacity: (!formData.FlowName || formData.levels.length === 0) ? 0.5 : 1 }}>
            {loading ? 'Saving...' : <><FaCheck /> {editingFlowId ? 'Update Workflow' : 'Create Workflow'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalFormModal;